/**
 * Shared API tracking state.
 * Extracted from ApiTracker.tsx to break circular dependency between
 * the React component and apiTrackingService.
 */

export interface ApiCall {
  api: string;
  count: number;
  cacheHits: number;
  lastCalled: Date;
  details: string;
}

// Global state for API calls
let apiCallsGlobal: { [key: string]: ApiCall } = {};
let updateCallbacks: Array<(calls: { [key: string]: ApiCall }) => void> = [];

// Global function to track API calls
export const trackApiCall = (api: string, details: string = '', isCache: boolean = false) => {
  const now = new Date();

  if (apiCallsGlobal[api]) {
    if (isCache) {
      apiCallsGlobal[api].cacheHits += 1;
    } else {
      apiCallsGlobal[api].count += 1;
    }
    apiCallsGlobal[api].lastCalled = now;
    apiCallsGlobal[api].details = details;
  } else {
    apiCallsGlobal[api] = {
      api,
      count: isCache ? 0 : 1,
      cacheHits: isCache ? 1 : 0,
      lastCalled: now,
      details,
    };
  }

  // Notify all components
  updateCallbacks.forEach(callback => callback({ ...apiCallsGlobal }));
};

// Global function to reset API tracking
export const resetApiTracking = () => {
  apiCallsGlobal = {};
  updateCallbacks.forEach(callback => callback({}));
};

// Global function to get current API calls
export const getCurrentApiCalls = () => ({ ...apiCallsGlobal });

// Internal helpers for the ApiTracker component — not part of the public API
export const __subscribeApiCalls = (cb: (calls: { [key: string]: ApiCall }) => void) => {
  updateCallbacks.push(cb);
  return () => {
    updateCallbacks = updateCallbacks.filter(c => c !== cb);
  };
};

export const __getApiCallsSnapshot = () => ({ ...apiCallsGlobal });
